import { useState } from 'react';
import { useSession } from '../../lib/auth/session.js';
import {
  useBillingAccount, useMovements, useAdjustBalance, useRateDecks, useRateRules, useRateTest,
} from '../../lib/api/hooks-billing.js';
import { Stat, TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { formatMoney, formatDateTime } from '../../lib/format.js';
import { ApiClientError } from '../../lib/api/client.js';

export function BillingPage() {
  const tenantId = useSession((s) => s.activeTenantId);
  const can = useSession((s) => s.can);
  const [tab, setTab] = useState<'overview' | 'ledger' | 'rates'>('overview');

  const account = useBillingAccount(tenantId);
  const showRates = can('ratedeck:read');

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Billing</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Balance, usage charges and rates
          </p>
        </div>
      </div>

      <div className="arc-tabs" role="tablist">
        <button type="button" role="tab" className="arc-tab" data-active={tab === 'overview'} onClick={() => setTab('overview')}>Overview</button>
        <button type="button" role="tab" className="arc-tab" data-active={tab === 'ledger'} onClick={() => setTab('ledger')}>Ledger</button>
        {showRates && (
          <button type="button" role="tab" className="arc-tab" data-active={tab === 'rates'} onClick={() => setTab('rates')}>Rates</button>
        )}
      </div>

      {tab === 'overview' && <Overview tenantId={tenantId} account={account} />}
      {tab === 'ledger' && <Ledger tenantId={tenantId} />}
      {tab === 'rates' && showRates && <Rates />}
    </>
  );
}

function Overview({ tenantId, account }: {
  tenantId: string | null;
  account: ReturnType<typeof useBillingAccount>;
}) {
  const can = useSession((s) => s.can);
  const [adjusting, setAdjusting] = useState(false);

  if (account.isLoading) {
    return <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 80 }} /></div>;
  }

  // A 404 here is a real answer, not an error: the tenant has no billing
  // account yet. Saying so beats showing a scary failure banner.
  if (account.error instanceof ApiClientError && account.error.status === 404) {
    return (
      <div className="arc-card">
        <Empty
          icon="⛁"
          title="No billing account"
          hint="This tenant has no billing account yet. Until one exists, calls cannot be rated or charged."
        />
      </div>
    );
  }
  if (account.error) return <ErrorBanner error={account.error} />;

  const a = account.data;
  if (!a) return null;

  const balance = Number(a.balance);
  const low = a.low_balance_threshold ? Number(a.low_balance_threshold) : null;
  const tone = balance <= 0 ? 'danger' : low !== null && balance < low ? 'warn' : undefined;

  return (
    <>
      {tone === 'danger' && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 16 }}>
          <span aria-hidden="true">⚠</span>
          <div>
            <div style={{ fontWeight: 600 }}>No available balance</div>
            <div>On a prepaid account, outbound calls are refused once the balance reaches zero.</div>
          </div>
        </div>
      )}
      {tone === 'warn' && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 16 }}>
          <span aria-hidden="true">⚠</span>
          <span>Balance is below the {formatMoney(a.low_balance_threshold, a.currency)} alert threshold.</span>
        </div>
      )}

      <div className="arc-grid arc-grid-4" style={{ marginBottom: 18 }}>
        <Stat label="Balance" value={formatMoney(a.balance, a.currency)} tone={tone} sub={a.mode} />
        <Stat label="Reserved" value={formatMoney(a.reserved, a.currency)} sub="held for calls in progress" />
        <Stat label="Available" value={formatMoney(a.available, a.currency)} sub="balance minus reserved" />
        <Stat
          label="Credit limit"
          value={formatMoney(a.credit_limit, a.currency)}
          sub={a.mode === 'prepaid' ? 'not used on prepaid' : 'postpaid headroom'}
        />
      </div>

      <div className="arc-card">
        <div className="arc-card-head">
          <h2 className="arc-h2">Account</h2>
          {can('balance:adjust') && (
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => setAdjusting((v) => !v)}>
              {adjusting ? 'Cancel' : 'Adjust balance'}
            </button>
          )}
        </div>
        <div className="arc-card-body">
          {adjusting && tenantId && <AdjustForm tenantId={tenantId} onDone={() => setAdjusting(false)} />}
          <dl className="arc-kv">
            <dt>Mode</dt><dd>{a.mode}</dd>
            <dt>Currency</dt><dd>{a.currency}</dd>
            <dt>VAT rate</dt><dd>{(Number(a.vat_rate) * 100).toFixed(0)}%</dd>
            <dt>Billing day</dt><dd>{a.billing_day} of each month</dd>
            <dt>Status</dt><dd>{a.status}</dd>
          </dl>
        </div>
      </div>
    </>
  );
}

function AdjustForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const adjust = useAdjustBalance(tenantId);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const err = adjust.error instanceof ApiClientError ? adjust.error : null;

  return (
    <form
      style={{ marginBottom: 18, paddingBottom: 18, borderBottom: '1px solid var(--arc-border)' }}
      onSubmit={(e) => {
        e.preventDefault();
        adjust.mutate({ amount, kind: 'topup', reason }, { onSuccess: () => { setAmount(''); setReason(''); onDone(); } });
      }}
    >
      {err && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <span aria-hidden="true">⚠</span>
          <div>
            <div>{err.message}</div>
            {/* Step-up is required for anything that moves money (doc 06 §4.4). */}
            {err.code === 'auth.step_up_required' && (
              <div style={{ marginTop: 4, fontSize: 12.5 }}>
                Re-verify your identity to continue. This action cannot be performed while impersonating.
              </div>
            )}
          </div>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: 12, alignItems: 'start' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="adj-amount">Amount</label>
          <input id="adj-amount" className="arc-input" inputMode="decimal" placeholder="500.00"
            value={amount} onChange={(e) => setAmount(e.target.value)} required />
          <div className="arc-hint">Positive credits, negative debits.</div>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="adj-reason">Reason</label>
          <input id="adj-reason" className="arc-input" placeholder="EFT received, ref 88213"
            value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} />
          <div className="arc-hint">Stored on the ledger and shown in the customer&apos;s audit view.</div>
        </div>
        <div style={{ paddingTop: 25 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={adjust.isPending}>
            {adjust.isPending ? 'Posting…' : 'Post'}
          </button>
        </div>
      </div>
    </form>
  );
}

function Ledger({ tenantId }: { tenantId: string | null }) {
  const movements = useMovements(tenantId);
  const tz = 'Africa/Johannesburg';

  return (
    <div className="arc-card">
      <div className="arc-card-head">
        <h2 className="arc-h2">Movements</h2>
        <span className="arc-subtle" style={{ fontSize: 12.5 }}>append-only ledger</span>
      </div>
      <div className="arc-table-wrap">
        <table className="arc-table">
          <thead>
            <tr><th>Date</th><th>Type</th><th>Description</th><th className="arc-td-right">Amount</th></tr>
          </thead>
          {movements.isLoading ? <TableSkeleton cols={4} /> : (
            <tbody>
              {movements.data?.data.map((m) => {
                const negative = m.amount.startsWith('-');
                return (
                  <tr key={m.id}>
                    <td className="arc-num arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(m.created_at, tz)}
                    </td>
                    <td><Badge tone={m.kind === 'call_charge' ? 'muted' : m.kind === 'topup' ? 'ok' : 'info'}>{m.kind.replace('_', ' ')}</Badge></td>
                    <td className="arc-muted">{m.description ?? '—'}</td>
                    <td className="arc-td-right arc-num" style={{ color: negative ? 'var(--arc-danger)' : 'var(--arc-ok)', fontWeight: 550 }}>
                      {formatMoney(m.amount, m.currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>
      {!movements.isLoading && movements.data?.data.length === 0 && (
        <Empty icon="⛁" title="No movements yet" hint="Charges and payments appear here." />
      )}
    </div>
  );
}

/**
 * The rate lookup tester (doc 07 section 4.10).
 *
 * "Small and disproportionately valuable" -- it settles a billing dispute in a
 * minute rather than an afternoon, because it shows which rule matched and
 * every step of the arithmetic. It calls the same engine the switch rates with.
 */
function Rates() {
  const decks = useRateDecks();
  const [deckId, setDeckId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const active = deckId ?? decks.data?.data[0]?.id ?? null;
  const rules = useRateRules(active, q);
  const test = useRateTest(active);

  const [destination, setDestination] = useState('+27821234567');
  const [duration, setDuration] = useState('60');

  return (
    <>
      <div className="arc-card" style={{ marginBottom: 16 }}>
        <div className="arc-card-head">
          <h2 className="arc-h2">Rate lookup tester</h2>
          <span className="arc-subtle" style={{ fontSize: 12.5 }}>
            same engine the switch rates with
          </span>
        </div>
        <div className="arc-card-body">
          <form
            style={{ display: 'grid', gridTemplateColumns: '1fr 140px 140px auto', gap: 12, alignItems: 'start' }}
            onSubmit={(e) => {
              e.preventDefault();
              test.mutate({ destination, duration_sec: Number(duration) || 60 });
            }}
          >
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="rt-dest">Destination</label>
              <input id="rt-dest" className="arc-input" value={destination}
                onChange={(e) => setDestination(e.target.value)} placeholder="+27821234567" />
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="rt-dur">Duration (s)</label>
              <input id="rt-dur" className="arc-input" inputMode="numeric" value={duration}
                onChange={(e) => setDuration(e.target.value)} />
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="rt-deck">Deck</label>
              <select id="rt-deck" className="arc-select" value={active ?? ''} onChange={(e) => setDeckId(e.target.value)}>
                {decks.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div style={{ paddingTop: 25 }}>
              <button type="submit" className="arc-btn arc-btn-primary" disabled={test.isPending}>
                {test.isPending ? '…' : 'Test'}
              </button>
            </div>
          </form>

          {test.data && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--arc-border)' }}>
              {test.data.ok ? (
                <>
                  <div className="arc-row" style={{ gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div className="arc-stat-label">Charge</div>
                      <div className="arc-stat-value" style={{ fontSize: 30 }}>
                        {formatMoney(test.data.charge, test.data.currency)}
                      </div>
                    </div>
                    <div>
                      <div className="arc-stat-label">Billable</div>
                      <div className="arc-stat-value" style={{ fontSize: 30 }}>{test.data.billable_seconds}s</div>
                    </div>
                  </div>
                  <dl className="arc-kv">
                    <dt>Matched prefix</dt>
                    <dd className="arc-mono">{test.data.breakdown?.matched_prefix}</dd>
                    <dt>Time band</dt><dd>{test.data.breakdown?.band}</dd>
                    <dt>Rate per minute</dt>
                    <dd>{formatMoney(test.data.breakdown?.rate_per_minute ?? null, test.data.currency)}</dd>
                    <dt>Connection fee</dt>
                    <dd>{formatMoney(test.data.breakdown?.connection_fee ?? null, test.data.currency)}</dd>
                    <dt>Raw / billable seconds</dt>
                    <dd className="arc-num">{test.data.breakdown?.raw_seconds}s → {test.data.breakdown?.billable_seconds}s</dd>
                    <dt>Minimum charge applied</dt>
                    <dd>{test.data.breakdown?.minimum_charge_applied ? 'yes' : 'no'}</dd>
                  </dl>
                </>
              ) : (
                <div className="arc-banner arc-banner-warn">
                  <span aria-hidden="true">⚠</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>{test.data.reason}</div>
                    <div style={{ marginTop: 3, fontSize: 12.5 }}>
                      An unrated destination is refused rather than billed at zero — letting it
                      through free is how you find out from the carrier invoice a month later.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {test.error && <ErrorBanner error={test.error} />}
        </div>
      </div>

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <input className="arc-input" placeholder="Filter by prefix…" value={q}
              onChange={(e) => setQ(e.target.value)} aria-label="Filter rate rules" />
          </div>
          <div className="arc-spacer" />
          {rules.data && (
            <span className="arc-subtle" style={{ fontSize: 12.5 }}>
              v{rules.data.version} {rules.data.is_published
                ? <Badge tone="ok">published</Badge>
                : <Badge tone="warn">draft</Badge>}
            </span>
          )}
        </div>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Prefix</th><th>Description</th><th>Band</th>
                <th className="arc-td-right">Per minute</th>
                <th className="arc-td-right">Connect</th>
                <th className="arc-td-right">Increments</th>
              </tr>
            </thead>
            {rules.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {rules.data?.data.map((r) => (
                  <tr key={r.id}>
                    <td className="arc-mono" style={{ fontWeight: 600 }}>+{r.prefix}</td>
                    <td>{r.description ?? '—'}</td>
                    <td><Badge tone="muted">{r.time_band}</Badge></td>
                    <td className="arc-td-right arc-num">{formatMoney(r.rate_per_minute, 'ZAR')}</td>
                    <td className="arc-td-right arc-num">{formatMoney(r.connection_fee, 'ZAR')}</td>
                    <td className="arc-td-right arc-num arc-subtle">
                      {r.first_increment_sec}/{r.subsequent_increment_sec}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!rules.isLoading && rules.data?.data.length === 0 && (
          <Empty icon="⛁" title="No rate rules" hint="Import a rate deck to price outbound calls." />
        )}
      </div>
    </>
  );
}
